import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Swiper from '~/ui/components/common/Swiper/Swiper';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';

const LinensSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$linensSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$linensSection' });

  const [isMobile, setIsMobile] = useState(false);
  const [hoveredColor, setHoveredColor] = useState<{ [key: number]: string | null }>({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Check on initial render
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const slides = [
    {
      id: 1,
      title: t('firstSlideTitle'),
      subtitle: t('firstSlideSubtitle'),
      description: t('firstSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-linens-signature-white.webp',
      imageHoverSrc: 'images/homePage/banners/hp-linens-signature-white_hover.webp',
      link: tLink('signature'),
      colors: {
        white: true,
        cream: false,
        blue: false,
        grey: false,
        whiteblue: false,
        bluewhite: false,
        beigewhite: false,
        whitebeige: false,
      },
      images: {
        white: 'images/homePage/banners/hp-linens-signature-white.webp',
      },
      alt: t('firstSlideAlt'),
    },
    {
      id: 2,
      title: t('secondSlideTitle'),
      subtitle: t('secondSlideSubtitle'),
      description: t('secondSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-linens-percale-platinum.webp',
      imageHoverSrc: 'images/homePage/banners/hp-linens-percale-platinum_hover.webp',
      link: tLink('percale'),
      colors: {
        white: true,
        cream: true,
        blue: true,
        grey: true,
        whiteblue: false,
        bluewhite: false,
        beigewhite: false,
        whitebeige: false,
      },
      images: {
        white: 'images/homePage/banners/hp-linens-percale-white.webp',
        cream: 'images/homePage/banners/hp-linens-percale-ivory.webp',
        blue: 'images/homePage/banners/hp-linens-percale-sapphire.webp',
        grey: 'images/homePage/banners/hp-linens-percale-platinum.webp',
      },
      alt: t('secondSlideAlt'),
    },
    {
      id: 3,
      title: t('thirdSlideTitle'),
      subtitle: t('thirdSlideSubtitle'),
      description: t('thirdSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-linens-sateen-sapphire.webp',
      imageHoverSrc: 'images/homePage/banners/hp-linens-sateen-sapphire_hover.webp',
      link: tLink('sateen'),
      colors: {
        white: true,
        cream: true,
        blue: true,
        grey: true,
        whiteblue: true,
        bluewhite: true,
        beigewhite: true,
        whitebeige: true,
      },
      images: {
        white: 'images/homePage/banners/hp-linens-sateen-white.webp',
        cream: 'images/homePage/banners/hp-linens-sateen-ivory.webp',
        blue: 'images/homePage/banners/hp-linens-sateen-sapphire.webp',
        grey: 'images/homePage/banners/hp-linens-sateen-platinum.webp',
        whiteblue: 'images/homePage/banners/hp-linens-sateen-whiteblue.webp',
        bluewhite: 'images/homePage/banners/hp-linens-sateen-bluewhite.webp',
        beigewhite: 'images/homePage/banners/hp-linens-sateen-beigewhite.webp',
        whitebeige: 'images/homePage/banners/hp-linens-sateen-whitebeige.webp',
      },
      alt: t('thirdSlideAlt'),
    },
  ];

  const [firstImgLoaded, setFirstImgLoaded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const renderSlide = (slide: any): ReactElement => (
    <Link href={slide.link} noTabbing data-event-name="bloc_interact" data-bloc-name={t('title')}>
      <div className="card-title-wrapper">
        <h3>{slide.title}</h3>
        <p className="card-subtitle">{slide.subtitle}</p>
      </div>
      <picture>
        <img
          loading={firstImgLoaded ? 'eager' : 'lazy'}
          data-src={hoveredColor[slide.id] ? slide.images[hoveredColor[slide.id]] : slide.imageSrc}
          alt={slide.alt}
          src={hoveredColor[slide.id] ? slide.images[hoveredColor[slide.id]] : slide.imageSrc}
          onLoad={() => {
            setFirstImgLoaded(true);
          }}
          width="1000"
          height="1022"
        />
      </picture>
      <p className="card-description">{slide.description}</p>
      <ul className="color-selector">
        {Object.keys(slide.colors).map(
          color =>
            slide.colors[color] && (
              <li key={color}>
                <span
                  className={color}
                  role="button"
                  tabIndex={0}
                  aria-label={`${color} color`}
                  onMouseEnter={() => setHoveredColor(prev => ({ ...prev, [slide.id]: color }))}
                  onMouseLeave={() => setHoveredColor(prev => ({ ...prev, [slide.id]: null }))}
                />
              </li>
            ),
        )}
      </ul>
    </Link>
  );

  const renderStaticSlides = (staticSlides: any[]): ReactElement => (
    <div className="swiper card-wrapper hp-swiper">
      <div className="swiper swiper-container swiper-wrapper swiper-container-initialized swiper-container-horizontal swiper-container-backface-hidden">
        <div className="swiper-wrapper">
          {staticSlides.map((slide, index) => (
            <div
              key={slide.id}
              className="swiper-slide card"
              data-event-name="bloc_interact"
              data-bloc-name={slide.title}
            >
              <Link href={slide.link} noTabbing>
                <div className="card-title-wrapper">
                  <h3>{slide.title}</h3>
                  <p className="card-subtitle">{slide.subtitle}</p>
                </div>
                <div className="hover-btn-animation">
                  <div
                    className="image-container"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <img
                      loading="lazy"
                      data-src={
                        hoveredColor[slide.id]
                          ? slide.images[hoveredColor[slide.id]]
                          : slide.imageSrc
                      }
                      alt={slide.alt}
                      src={
                        hoveredColor[slide.id]
                          ? slide.images[hoveredColor[slide.id]]
                          : slide.imageSrc
                      }
                      width="1000"
                      height="1022"
                    />
                    <img
                      data-src={slide.imageHoverSrc}
                      alt={slide.title}
                      src={slide.imageHoverSrc}
                      loading="lazy"
                      width="1000"
                      height="1022"
                      className={`hover ${hoveredIndex === index ? 'hover-active' : ''}`}
                    />
                  </div>
                  <div className="hover-content desktop-only">
                    <span className="btn base-btn btn-main">
                      <span>{tButton('shopNow')}</span>
                    </span>
                  </div>
                </div>
                <p className="card-description">{slide.description}</p>
                <ul className="color-selector">
                  {Object.keys(slide.colors).map(
                    color =>
                      slide.colors[color] && (
                        <li key={color}>
                          <span
                            className={color}
                            role="button"
                            tabIndex={0}
                            aria-label={`${color} color`}
                            onMouseEnter={() =>
                              setHoveredColor(prev => ({ ...prev, [slide.id]: color }))
                            }
                            onMouseLeave={() =>
                              setHoveredColor(prev => ({ ...prev, [slide.id]: null }))
                            }
                          />
                        </li>
                      ),
                  )}
                </ul>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const slideClassName = (): string => 'card';

  return (
    <section id="hp-linens" className="hp-section linens-section linens">
      <div
        className="hp-banner transparent end"
        data-event-name="bloc_interact"
        data-bloc-name={t('title')}
      >
        <Link href={tLink('linens')} className="clearfix">
          <picture>
            <source
              data-srcset="images/homePage/mobile/hp-banner-linens-mob.webp"
              media="(max-width: 768px)"
              srcSet="images/homePage/mobile/hp-banner-linens-mob.webp"
            />
            {/* Intentionaly left as jpg: Webp image looked low quality. */}
            <img
              loading="lazy"
              data-src="images/homePage/banners/hp-banner-linens.webp"
              alt={`${t('title')} banner`}
              src="images/homePage/banners/hp-banner-linens.webp"
              width="1440"
              height="676"
            />
          </picture>
          <div className="stack">
            <h2>{t('title')}</h2>
            <p className="desktop-only">{t('copyDesktop')}</p>
            <p className="mobile-only">{t('copyMobile')}</p>
            <span className="btn base-btn btn-main">
              <span>{t('shopLinens')}</span>
            </span>
          </div>
        </Link>
      </div>
      <div className="carousel-wrapper">
        {isMobile ? (
          <div className="swiper card-wrapper hp-swiper">
            <Swiper
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              slides={slides}
              renderSlide={renderSlide}
              slideClassName={slideClassName}
              navigation // Include navigation
              pagination // Include pagination
              slidesPerView={1}
              slidesPerGroup={1} // Move 'X' slides at a time
              spaceBetween={14} // Add space between slides
              touchEventsTarget="container"
              containerModifierClass="swiper-container-"
              className="swiper-container swiper-wrapper"
              breakpoints={{
                // when window width is >= 370px
                370: {
                  slidesPerView: 1.2,
                  slidesPerGroup: 1,
                  spaceBetween: 14,
                },
              }}
            />
          </div>
        ) : (
          renderStaticSlides(slides)
        )}
      </div>
    </section>
  );
};

export default LinensSection;
